import type React from "react";
import { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { type TierDetails } from "~/api/types";
import { ChartReorderContext } from "./DocumentCard";
import { type ChartColumn, type ChartData } from "../helpers/chart";
import { type DocumentSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { Info } from "lucide-react";
import { CategoryModal } from "./category-modal/CategoryModal";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { updateDocumentOrder } from "~/api/v2/requests";
import { TierRow, type OnDragEnd } from "./TierRow";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

interface ChartDocumentsProps {
  data: ChartData;
  tiers: TierDetails[];
  hideCategories: boolean;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
  onCategoryModalClose: () => void;
  category?: { id: number; title: string; description?: string };
}

const CategoryInfoAction = ({
  category,
  canManage,
  onCategoryModalClose,
}: {
  category: NonNullable<ChartDocumentsProps["category"]>;
  canManage: boolean;
  onCategoryModalClose: () => void;
}) => {
  const trigger = canManage ? (
    <Button
      variant="ghost"
      size="icon"
      className="border-0 focus-visible:ring-0"
    >
      <Info className="size-5 opacity-50" />
    </Button>
  ) : (
    <span className="inline-flex size-9 items-center justify-center">
      <Info className="size-5 opacity-50" />
    </span>
  );

  const triggerWithTooltip = category.description ? (
    <TooltipTrigger asChild>{trigger}</TooltipTrigger>
  ) : (
    trigger
  );

  const content = canManage ? (
    <CategoryModal
      categoryId={category.id}
      trigger={triggerWithTooltip}
      onClose={onCategoryModalClose}
    />
  ) : (
    triggerWithTooltip
  );

  if (!category.description) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        {content}
        <TooltipContent>
          <p>{category.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ChartDocuments = ({
  data,
  tiers,
  hideCategories,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
  onCategoryModalClose,
  category,
}: ChartDocumentsProps) => {
  const [reorderKey, setReorderKey] = useState(0);

  const notifyReorderStart = () => {
    setReorderKey((k) => k + 1);
  };

  return (
    <ChartReorderContext.Provider value={{ reorderKey, notifyReorderStart }}>
      <div className="min-w-37.5 border-l">
        <div
          className={
            category
              ? "sticky left-64 z-10 flex h-9 w-fit items-center px-4 text-sm"
              : "h-9"
          }
        >
          {category && (
            <div className="flex items-center gap-1">
              <div className="font-medium whitespace-nowrap">
                {category.title}
              </div>
              <CategoryInfoAction
                category={category}
                canManage={canManage}
                onCategoryModalClose={onCategoryModalClose}
              />
            </div>
          )}
        </div>
        {data.mode === "graph" ? (
          <GraphLayout
            columns={data.columns}
            tiers={tiers}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            setSelectedId={setSelectedId}
          />
        ) : (
          <GridLayout
            column={data.column}
            tiers={tiers}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            setSelectedId={setSelectedId}
            canManage={canManage && !hideCategories}
            onReorder={onReorder}
          />
        )}
      </div>
    </ChartReorderContext.Provider>
  );
};

interface GraphLayoutProps {
  columns: ChartColumn[];
  tiers: TierDetails[];
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
}

const GraphLayout = ({
  columns,
  tiers,
  documentRefsMap,
  containerRef,
  setSelectedId,
}: GraphLayoutProps) => {
  const [hoveredId, setHoveredId] = useState<number | undefined>();

  const columnsToRender: ChartColumn[] =
    columns.length > 0 ? columns : [new Map<number, DocumentSummary[]>()];

  return (
    <div className="flex">
      {columnsToRender.map((columnMap, columnIndex) => (
        <div key={columnIndex} className="flex w-full flex-col">
          {tiers.map((tier) => (
            <TierRow
              key={tier.id}
              documents={columnMap.get(tier.id) ?? []}
              documentRefsMap={documentRefsMap}
              containerRef={containerRef}
              hoveredId={hoveredId}
              setHoveredId={setHoveredId}
              setSelectedId={setSelectedId}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

interface GridLayoutProps {
  column: ChartColumn;
  tiers: TierDetails[];
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const GridLayout = ({
  column,
  tiers,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
}: GridLayoutProps) => {
  const [hoveredId, setHoveredId] = useState<number | undefined>();

  const widestTierSize = Math.max(
    0,
    ...Array.from(column.values(), (tierVersions) => tierVersions.length),
  );

  return (
    <div className="flex flex-col">
      {tiers.map((tier) => (
        <GridTierRow
          key={tier.id}
          tierId={tier.id}
          documents={column.get(tier.id) ?? []}
          widestTierSize={widestTierSize}
          documentRefsMap={documentRefsMap}
          containerRef={containerRef}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          setSelectedId={setSelectedId}
          canManage={canManage}
          onReorder={onReorder}
        />
      ))}
    </div>
  );
};

interface GridTierRowProps {
  tierId: number;
  documents: DocumentSummary[];
  widestTierSize: number;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const GridTierRow = ({
  tierId,
  documents,
  widestTierSize,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
  canManage,
  onReorder,
}: GridTierRowProps) => {
  const { t } = useTranslation();
  const { notifyReorderStart } = useContext(ChartReorderContext);
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [prevDocumentsProp, setPrevDocumentsProp] = useState(documents);

  if (prevDocumentsProp !== documents) {
    setPrevDocumentsProp(documents);
    setLocalDocuments(documents);
  }

  const sortableType = `DOC_${tierId}`;

  const handleDragEnd = useCallback<OnDragEnd>(
    (event) => {
      const handle = async () => {
        if (event.canceled) return;
        if (!isSortableOperation(event.operation)) return;

        const source = event.operation.source;
        if (!source) return;

        const { initialIndex, index: targetIndex } = source;
        if (initialIndex === targetIndex) return;

        const reordered = moveItemByIndex(
          localDocuments,
          initialIndex,
          targetIndex,
        );
        setLocalDocuments(reordered);
        notifyReorderStart();

        const orderPayload = reordered.map((document, i) => ({
          id: document.id,
          chartOrder: i,
        }));

        try {
          await updateDocumentOrder(orderPayload);
          onReorder();
        } catch {
          toast.error(t(($) => $.toasts.error.reorderDocuments));
          setLocalDocuments(documents);
        }
      };

      void handle();
    },
    [documents, localDocuments, notifyReorderStart, onReorder, t],
  );

  const sortable =
    canManage && localDocuments.length > 1
      ? { type: sortableType, onDragEnd: handleDragEnd }
      : undefined;

  return (
    <TierRow
      documents={localDocuments}
      linesDocuments={documents}
      documentRefsMap={documentRefsMap}
      containerRef={containerRef}
      hoveredId={hoveredId}
      setHoveredId={setHoveredId}
      setSelectedId={setSelectedId}
      emptySlots={Math.max(0, widestTierSize - localDocuments.length)}
      sortable={sortable}
    />
  );
};

export default ChartDocuments;
